import React, { useMemo } from 'react';
import { useData } from 'resift';
import _get from 'lodash/get';
import classNames from 'classnames';
import makeGetMappingsAttributes from 'fetches/makeGetMappingsAttributes';
import sampleDataLookup from 'components/Mappings/_helpers/sampleDataLookup';

import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import TableCell from '@material-ui/core/TableCell';
import Collapse from '@material-ui/core/Collapse';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import SourceSelector from 'components/Mappings/SourceSelector';
import CollectionMapping, { MAPPING_PRESETS } from 'components/Mappings/CollectionMapping';
import {
  ORG_TEAM_LEADER_MAPPING_REF_KEY,
  DEFAULT_EMPTY_ORG_MAPPINGS,
  CSV_SOURCE_TYPE_KEY,
} from 'pages/Directory/consts';
import sourceTypes from 'consts/sourceTypes';
import MappingProps from 'types/Mapping';
import SourceType from 'types/Source';

interface Props {
  mappingIndex: number;
  mapping?: MappingProps;
  disabled?: boolean;
  source: SourceType;
  destination: 'tlRef' | 'tlMatch';
  onUpdateMapping: (newMappingIndex: number, newMapping: MappingProps) => void;
}

function OrgSourceMapping({
  mappingIndex,
  source,
  destination,
  mapping = {} as MappingProps,
  onUpdateMapping,
  disabled,
}: Props) {
  const classes = useStyles();
  const value = _get(mapping, 'source', '');
  const rootName = value.split('.')[0];
  const getMappingsAttributes = makeGetMappingsAttributes(source.id);
  const mappingAttributes = useData(getMappingsAttributes);
  const sampleUserData = _get(mappingAttributes, 'sampleData', {});
  const isGoogle = sourceTypes.isGoogle(source.type);
  const isCollapsed = useMemo(() => {
    return !!MAPPING_PRESETS.find(MP => MP.key === rootName && isGoogle);
  }, [rootName]);
  const tlName = isCollapsed ? rootName : value;
  const _sampleData = isCollapsed
    ? sampleDataLookup(sampleUserData, mapping)
    : _get(sampleUserData, mapping.source);
  const sampleData = _sampleData || '(none)';
  const noSampleData = sampleData === '(none)';
  const isCsvSource = source.type === CSV_SOURCE_TYPE_KEY;

  const handleUpdateSource = (sourceValue: string) => {
    const newMapping: MappingProps = {
      ...mapping,
      source: sourceValue,
      type: 'logistical',
      collectionItemType: null,
      collectionItemCustomType: null,
      destination,
      customProps: {
        ...mapping.customProps,
      },
    };

    onUpdateMapping(mappingIndex, newMapping);
  };

  const handleCollectionChange = (newMapping: MappingProps) => {
    onUpdateMapping(mappingIndex, {
      ...newMapping,
      destination,
    });
  };

  const handleClearMapping = () => {
    const defaultMapping =
      destination === ORG_TEAM_LEADER_MAPPING_REF_KEY
        ? DEFAULT_EMPTY_ORG_MAPPINGS[1]
        : DEFAULT_EMPTY_ORG_MAPPINGS[0];

    onUpdateMapping(mappingIndex, {
      ...defaultMapping,
      destination,
    });
  };

  return (
    <>
      <TableRow className={classNames({ [classes.disabledRow]: disabled })}>
        <TableCell>
          <Typography variant="caption" color="textSecondary">
            {destination === ORG_TEAM_LEADER_MAPPING_REF_KEY ? 'Supervisor' : 'Employee'}{' '}
            {isCsvSource ? 'Header' : 'Property'}
          </Typography>
          <SourceSelector
            onChange={handleUpdateSource}
            value={value}
            isSourceCollapsed={isCollapsed}
            isCsvSource={isCsvSource}
          />
        </TableCell>
        <TableCell
          title={sampleData}
          className={classNames(classes.ellipsisText, noSampleData && classes.noSampleData)}
        >
          {sampleData}
        </TableCell>
        <TableCell>{value && <Button onClick={handleClearMapping}>Clear</Button>}</TableCell>
      </TableRow>
      {isCollapsed && (
        <TableRow className={classes.collectionMappingRow}>
          <TableCell colSpan={3}>
            <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
              <CollectionMapping
                mapping={mapping}
                value={tlName}
                onChange={handleCollectionChange}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  ellipsisText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  noSampleData: {
    color: theme.palette.text.disabled,
  },
  disabledRow: {
    pointerEvents: 'none',
    color: theme.palette.text.disabled,
  },
  collectionMappingRow: {
    backgroundColor: theme.palette.grey[100],
  },
}));

export default OrgSourceMapping;
